import React, { useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { isEmpty, uniqBy } from "lodash-es";

import {
  ActionBar,
  apiShape,
  Column,
  Container,
  Heading,
  intlShape,
  isTouchDevice,
  List,
  Loader,
  notifierShape,
  PaymentRequiredError,
  Row,
  SearchBar,
  withAccessRights,
  withApi,
  withIntl,
  withNotifier,
} from "lcm-iot-commons";

import SubscriptionAssignedAssetsEditItem from "lcm-iot-commons/client/lib/components/Subscriptions/SubscriptionAssignedAssetsEditItem";

type Props = {
  api: apiShape;
  intl: intlShape;
  notifier: notifierShape;
  match: {
    params: {
      id?: string;
    };
  };
};

export function ConnectSubscriptionAssignedAssetsEdit({
  api,
  intl,
  match,
  notifier,
}: Props) {
  const [data, setData] = useState();
  const [searchResult, setSearchResult] = useState();
  const [fetching, setFetching] = useState(true);
  const [updating, setUpdating] = useState(false);

  const search = async (assignedAssetIds: $TSFixMe, query: $TSFixMe) => {
    try {
      setFetching(true);
      const response = await api.get("/search", {
        q: isEmpty(query) ? "*" : query,
        scope: "assets",
      });
      setSearchResult({
        // search result is not unique, so we need to filter out duplicates
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ assets: { toString: () => stri... Remove this comment to see the full error message
        assets: uniqBy(response.results, "id"),
        nextPageUrl: response.pagination.next,
      });
      setFetching(false);
    } catch (error) {
      notifier.showError(api.translateError(error));
    }
  };

  const loadData = async () => {
    try {
      const response = await api.getAll(
        `/api_subscriptions/${match.params.id}/assets`,
        { include: "product,product.manufacturer,product.tenant" },
      );
      const assignedAssetIds = response.assets.map(
        (asset: $TSFixMe) => asset.id,
      );

      setData({
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ assignedAssetIds: any; assigne... Remove this comment to see the full error message
        assignedAssetIds,
        assignedAssetCount: assignedAssetIds.length,
      });

      // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
      await search(assignedAssetIds);
    } catch (error) {
      notifier.showError(api.translateError(error));
    }
  };

  const handleLoadMore = async () => {
    // only trigger this function if there is a next page
    if (!(searchResult as $TSFixMe)?.nextPageUrl) return;

    try {
      setFetching(true);
      const response = await api.get((searchResult as $TSFixMe).nextPageUrl);
      setSearchResult({
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ assets: any[]; nextPageUrl: an... Remove this comment to see the full error message
        assets: [
          ...(searchResult as $TSFixMe).assets,
          ...response.results.map((result: $TSFixMe) => ({
            ...result,
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            assigned: data.assignedAssetIds.indexOf(result.id) >= 0,
          })),
        ],
        nextPageUrl: response.pagination.next,
      });
      setFetching(false);
    } catch (error) {
      notifier.showError(api.translateError(error));
    }
  };

  const handleOnSearch = async (query: $TSFixMe) => {
    /* istanbul ignore if */
    if (!data) return;
    await search((data as $TSFixMe).assignedAssetIds, query);
  };

  const handleOnChange = async (
    assetId: $TSFixMe,
    currentInSubscription: $TSFixMe,
  ) => {
    setUpdating(true);
    try {
      if (currentInSubscription) {
        await api.delete(`/api_subscriptions/${match.params.id}/assets`, {
          assets: [{ id: assetId }],
        });
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        const assignedAssetIds = data.assignedAssetIds.filter(
          (id: $TSFixMe) => id !== assetId,
        );
        setData({
          // @ts-expect-error TS(2345) FIXME: Argument of type '{ assignedAssetIds: any; assigne... Remove this comment to see the full error message
          assignedAssetIds,
          assignedAssetCount: assignedAssetIds.length,
        });
        notifier.showSuccess(
          intl.formatMessage({
            id: "subscription.asset_unassigned_success_message",
          }),
        );
      } else {
        await api.post(`/api_subscriptions/${match.params.id}/assets`, {
          assets: [{ id: assetId }],
        });
        setData({
          // @ts-expect-error TS(2345) FIXME: Argument of type '{ assignedAssetIds: any[]; assig... Remove this comment to see the full error message
          assignedAssetIds: [...data.assignedAssetIds, assetId],
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          assignedAssetCount: data.assignedAssetCount + 1,
        });
        notifier.showSuccess(
          intl.formatMessage({
            id: "subscription.asset_assigned_success_message",
          }),
        );
      }
    } catch (error) {
      if (error instanceof PaymentRequiredError) {
        notifier.showError(
          intl.formatMessage({
            id: "subscription.connected_asset.quota_exceed_message",
          }),
        );
      } else {
        notifier.showError(api.translateError(error));
      }
    }
    setUpdating(false);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const title = data
    ? `${intl.formatMessage({ id: "subscription.assigned_asset_edit" })} (${(data as $TSFixMe).assignedAssetCount})`
    : intl.formatMessage({ id: "subscription.assigned_asset_edit" });

  return (
    <Loader loading={fetching}>
      <Container>
        <Row>
          <Column>
            <ActionBar>
              <Heading title={title} />
            </ActionBar>
          </Column>
        </Row>
        <Row>
          <Column>
            <SearchBar
              // @ts-expect-error TS(2322) FIXME: Type '{ autoFocus: boolean; disabled: boolean; fil... Remove this comment to see the full error message
              autoFocus={!isTouchDevice()}
              disabled={!searchResult}
              filterWildcard={false}
              minChars={1}
              onSearch={handleOnSearch}
              placeholder={intl.formatMessage({
                id: "search.searchbox_placeholder",
              })}
              resettable
              timeout={800}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <List id="assets-list">
              {searchResult && (
                <InfiniteScroll
                  data-testid="infinite-scroll"
                  initialLoad={false}
                  loadMore={handleLoadMore}
                  hasMore={
                    !fetching && !!(searchResult as $TSFixMe)?.nextPageUrl
                  }
                >
                  {(searchResult as $TSFixMe).assets.map((asset: $TSFixMe) => (
                    <SubscriptionAssignedAssetsEditItem
                      key={asset.id}
                      id={asset.id}
                      title={asset.title}
                      currentInSubscription={
                        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                        data.assignedAssetIds.indexOf(asset.id) >= 0
                      }
                      description={asset.description}
                      pictureUrl={asset.picture_url}
                      disabled={fetching || updating}
                      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                      checked={data.assignedAssetIds.includes(asset.id)}
                      onChange={handleOnChange}
                    />
                  ))}
                </InfiniteScroll>
              )}
            </List>
          </Column>
        </Row>
      </Container>
    </Loader>
  );
}

export default withIntl(
  withApi(
    withNotifier(
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ (props: Readonly<Omit<WithAcce... Remove this comment to see the full error message
      withAccessRights(
        // @ts-expect-error TS(2345) FIXME: Argument of type '({ api, intl, match, notifier, }... Remove this comment to see the full error message
        ConnectSubscriptionAssignedAssetsEdit,
        "APISubscription",
      ),
    ),
  ),
);
